import useSWR from 'swr'
import { useLocation } from 'react-router'
import { useAuthToken } from '@/hooks/useAuthToken'
import { useOrgGroupAccess } from '@/hooks/useOrgGroupAccess'
import { useNeedsAnalysisStatus } from '@/hooks/useNeedsAnalysisStatus'
import { assessmentService } from '@/services/assessmentService'
import { FeatureAccessPath } from '@/types/configuration'
import type { LatestProficiencyResponse } from '@/types/assessment'
import { useRole } from '@/hooks/useRole'
import { ClerkUserRole } from '@/types/auth'

// By default new learners get a week to try lessons before the assessment (which
// can run over an hour) is forced. Counted from needs analysis completion, which
// is when the modal would otherwise first appear. Orgs and groups override it
// with assessments.mandatory_grace_days; 0 forces it right after the needs
// analysis (on-ramp programs). It stays available from the homepage quick links
// and the onboarding checklist during the grace period.
export const DEFAULT_MANDATORY_ASSESSMENT_GRACE_DAYS = 7

function resolveGraceDays(configValue: unknown): number {
  return typeof configValue === 'number' && Number.isFinite(configValue) && configValue >= 0
    ? configValue
    : DEFAULT_MANDATORY_ASSESSMENT_GRACE_DAYS
}

type UseMandatoryAssessmentRequiredReturn = {
  readonly shouldShowMandatoryAssessment: boolean
  // When the modal starts showing, while an assessment is required and not yet
  // taken (including during the grace period). Null when none is required, or
  // when the start date is unknown and the modal shows immediately.
  readonly assessmentDueAt: Date | null
  readonly isLoading: boolean
}

function gracePeriodEnd(needsAnalysisCompletedAt: string | null | undefined, graceDays: number): Date | null {
  if (!needsAnalysisCompletedAt) return null
  const completedAt = new Date(needsAnalysisCompletedAt)
  if (Number.isNaN(completedAt.getTime())) return null
  return new Date(completedAt.getTime() + graceDays * 24 * 60 * 60 * 1000)
}

export function useMandatoryAssessmentRequired(): UseMandatoryAssessmentRequiredReturn {
  // Subscribing to the location re-renders every consumer on navigation, which
  // re-reads the clock below. That is when the grace period is allowed to end:
  // the guard opens the modal on the learner's next page change, never mid-lesson,
  // and the Userpilot identify (mounted in Root, which nothing else re-renders on
  // a clock boundary) flips onboarding_stage to 'assessment' at the same moment.
  useLocation()
  const { getToken, isSignedIn } = useAuthToken()
  const { userRole } = useRole()

  // Client admins should never see the mandatory assessment modal
  const isClientAdmin = userRole === ClerkUserRole.CLIENT_ADMIN

  // Check if assessments feature is enabled for this org/group
  const { configValue: isAssessmentsEnabledValue, isLoading: isAssessmentsEnabledLoading } = useOrgGroupAccess({
    configPath: FeatureAccessPath.ASSESSMENTS_ENABLED,
  })

  // Check if assessments are mandatory for this org/group
  const { configValue: isMandatoryConfigValue, isLoading: isMandatoryConfigLoading } = useOrgGroupAccess({
    configPath: FeatureAccessPath.ASSESSMENTS_MANDATORY,
  })

  const { configValue: graceDaysConfigValue, isLoading: isGraceDaysLoading } = useOrgGroupAccess({
    configPath: FeatureAccessPath.ASSESSMENTS_MANDATORY_GRACE_DAYS,
  })

  const isConfigLoading = isAssessmentsEnabledLoading || isMandatoryConfigLoading || isGraceDaysLoading

  // Modal should only show if assessments are both enabled AND mandatory
  const isMandatoryEnabled = isMandatoryConfigValue === true && isAssessmentsEnabledValue === true

  const { hasCompletedNeedsAnalysis, needsAnalysisData, isLoading: isNeedsAnalysisLoading } = useNeedsAnalysisStatus()

  const {
    data: latestProficiency,
    error: proficiencyError,
    isLoading: isProficiencyLoading,
  } = useSWR<LatestProficiencyResponse | null>(
    isSignedIn ? '/assessment/latest-proficiency' : null,
    async () => assessmentService.getLatestProficiency(getToken),
    { revalidateOnFocus: false, errorRetryCount: 3, dedupingInterval: 60 * 1000 }
  )

  const isLoading = isConfigLoading || isNeedsAnalysisLoading || isProficiencyLoading

  if (isLoading) {
    return { shouldShowMandatoryAssessment: false, assessmentDueAt: null, isLoading: true }
  }

  // Client admins are exempt from mandatory assessment requirement
  if (isClientAdmin) {
    return { shouldShowMandatoryAssessment: false, assessmentDueAt: null, isLoading: false }
  }

  // Fail-safe: treat a proficiency lookup error as "not taken yet". Hiding the
  // modal on error would silently let learners bypass mandatory assessments.
  const hasNoProficiency = !!proficiencyError || latestProficiency === null
  const isRequired = isMandatoryEnabled && hasCompletedNeedsAnalysis && hasNoProficiency
  if (!isRequired) {
    return { shouldShowMandatoryAssessment: false, assessmentDueAt: null, isLoading: false }
  }

  // A missing or unparseable completion date forces the modal now rather than
  // granting an open-ended grace period.
  const dueAt = gracePeriodEnd(needsAnalysisData?.completed_at, resolveGraceDays(graceDaysConfigValue))
  const shouldShowMandatoryAssessment = !dueAt || Date.now() >= dueAt.getTime()
  return { shouldShowMandatoryAssessment, assessmentDueAt: dueAt, isLoading: false }
}
